import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { APIClient } from "../../helpers/api_helper";

const api = new APIClient();

export const fetchUnreadNotifications = createAsyncThunk(
  "notifications/fetchUnread",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/notifications/unread");
      console.log("Unread notifications response:", response);
      return response;
    } catch (error) {
      console.error("Fetch unread notifications error:", error);
      return rejectWithValue(error.response?.data || { error: "Failed to load notifications" });
    }
  }
);

export const fetchAllNotifications = createAsyncThunk(
  "notifications/fetchAll",
  async (limit = 50, { rejectWithValue }) => {
    try {
      const response = await api.get(`/notifications?limit=${limit}`);
      console.log("All notifications response:", response);
      return response;
    } catch (error) {
      console.error("Fetch all notifications error:", error);
      return rejectWithValue(error.response?.data || { error: "Failed to load notifications" });
    }
  }
);

export const getUnreadCount = createAsyncThunk(
  "notifications/getCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/notifications/count");
      console.log("Unread count response:", response);
      return response;
    } catch (error) {
      console.error("Fetch unread count error:", error);
      return rejectWithValue(error.response?.data || { error: "Failed to load count" });
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  "notifications/markRead",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await api.put(`/notifications/${notificationId}/read`, {});
      console.log("Mark read response:", response);
      return { id: notificationId, ...response };
    } catch (error) {
      console.error("Mark read error:", error);
      return rejectWithValue(error.response?.data || { error: "Failed to mark as read" });
    }
  }
);

export const markAllAsRead = createAsyncThunk(
  "notifications/markAllRead",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.put("/notifications/read-all", {});
      console.log("Mark all read response:", response);
      return response;
    } catch (error) {
      console.error("Mark all read error:", error);
      return rejectWithValue(error.response?.data || { error: "Failed to mark all as read" });
    }
  }
);

export const deleteNotification = createAsyncThunk(
  "notifications/delete",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/notifications/${notificationId}`);
      console.log("Delete notification response:", response);
      return { id: notificationId };
    } catch (error) {
      console.error("Delete notification error:", error);
      return rejectWithValue(error.response?.data || { error: "Failed to delete notification" });
    }
  }
);

const initialState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    clearNotifications: (state) => {
      state.notifications = [];
    },
    addNotificationOptimistic: (state, action) => {
      // Add notification to the beginning of the list when received via WebSocket or polling
      state.notifications.unshift(action.payload);
      state.unreadCount += 1;
    },
    removeNotificationLocal: (state, action) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch unread notifications
      .addCase(fetchUnreadNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnreadNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload?.data?.notifications || [];
        state.unreadCount = action.payload?.data?.count || 0;
      })
      .addCase(fetchUnreadNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Failed to load notifications";
      })

      // Fetch all notifications
      .addCase(fetchAllNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload?.data?.notifications || [];
      })
      .addCase(fetchAllNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Failed to load notifications";
      })

      // Get unread count
      .addCase(getUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload?.data?.unreadCount || 0;
      })

      // Mark as read
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload.id);
        if (notification) {
          notification.isRead = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })

      // Mark all as read
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.notifications.forEach(n => {
          n.isRead = true;
        });
        state.unreadCount = 0;
      })

      // Delete notification
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload.id);
        if (notification) {
          state.notifications = state.notifications.filter(n => n.id !== action.payload.id);
          if (!notification.isRead) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
      });
  },
});

export const { clearNotifications, addNotificationOptimistic, removeNotificationLocal } = notificationSlice.actions;
export default notificationSlice.reducer;
